import { IBulkPostsPayload, usePostsRequest, IWorkspacePostsResponse } from "../../apis/posts/usePostsRequest";
import { transformProfilePosts } from "../../../utils";
import { ITransformedPost } from "../../apis/posts/usePostsRequest";
import { useEffect, useRef, useCallback } from "react";
import { useAuthStore } from "../../../stores/auth/useAuthStore";
import { useAuthHandlers } from "../auth/useAuthHandlers";
import { useExtensionRequest } from "../../apis/extension/useExtensionRequest";
import { useAppHandlers } from "../app/useAppHandlers";

// Constants for pause intervals (60s to 150s)
const PAUSE_MIN_SECONDS = parseInt(process.env.REACT_APP_PAUSE_MIN_SECONDS || '60');
const PAUSE_MAX_SECONDS = parseInt(process.env.REACT_APP_PAUSE_MAX_SECONDS || '150');

// Configurable fetch interval in minutes
const FETCH_INTERVAL_MINUTES = parseInt(process.env.REACT_APP_FETCH_INTERVAL_MINUTES || '1');

// Configurable trigger interval in seconds (how often to check for posts fetch)
const TRIGGER_INTERVAL_SECONDS = parseInt(process.env.REACT_APP_TRIGGER_INTERVAL_SECONDS || '10');

/**
 * Sleep utility function for pausing execution
 * @param ms - milliseconds to sleep
 */
const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Generate a random pause duration between min and max seconds
 * @returns random number of milliseconds
 */
const getRandomPauseDuration = (): number => {
    const minMs = PAUSE_MIN_SECONDS * 1000;
    const maxMs = PAUSE_MAX_SECONDS * 1000;
    return Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;
};

/** Short pause between profiles for manual feed sync (background sync keeps longer pauses). */
const MANUAL_FEED_SYNC_PAUSE_MS_MIN = 2_000;
const MANUAL_FEED_SYNC_PAUSE_MS_MAX = 4_000;

const getManualFeedSyncPauseMs = (): number =>
    Math.floor(
        Math.random() * (MANUAL_FEED_SYNC_PAUSE_MS_MAX - MANUAL_FEED_SYNC_PAUSE_MS_MIN + 1),
    ) + MANUAL_FEED_SYNC_PAUSE_MS_MIN;

export type TFeedSyncProfile = {
    profileId?: string | null;
    publicIdentifier?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    name?: string | null;
};

export type TFeedSyncProgress = {
    index: number;
    total: number;
    displayName: string;
    publicIdentifier?: string;
    status: 'syncing' | 'success' | 'skipped' | 'failed';
    synced: number;
    failed: number;
    skipped: number;
};

/** Same resolution as CreateFeedModal fetchPostsForConnections. */
function resolveFeedSyncIds(profile: TFeedSyncProfile & { profile?: { profileId?: string; publicIdentifier?: string } }) {
    const publicIdentifier = String(
        profile.publicIdentifier || profile.profile?.publicIdentifier || '',
    ).trim();
    const profileId = String(
        profile.profileId || profile.profile?.profileId || '',
    ).trim();
    return { publicIdentifier, profileId };
}

export const usePostsHandlers = () => {

    const { createOrUpdateBulkPosts, getWorkspacePosts, getProfilesWithTrackedConnections, updateConnectionPosts, updateWeeklyGoal } = usePostsRequest();
    const { store: { user } } = useAuthStore();
    const { updateLastFetchPostsDateHandler } = useAuthHandlers();
    const { getProfilePosts, likeProfilePosts, commentProfilePosts, editProfilePostComment, deleteProfilePostComment } = useExtensionRequest();
    const { showErrorToast } = useAppHandlers();

    const userRef = useRef(user);
    const isExecutingRef = useRef(false);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const createOrUpdateBulkPostsHandler = async (postsArray: ITransformedPost[], publicIdentifier?: string) => {
        const payload: IBulkPostsPayload = {
            posts: postsArray,
            publicIdentifier
        };
        const result = await createOrUpdateBulkPosts(payload);
        
        return result;
    }


    /**
     * Transform raw LinkedIn posts and send to backend
     */
    const syncProfilePostsHandler = async (lnPosts: any, publicIdentifier: string) => {
        try {
            // Transform LinkedIn posts to our format
            const transformedPosts = transformProfilePosts(lnPosts);
            
            if (transformedPosts.length === 0) {
                console.log("No posts to sync");
                return null;
            }
            
            // Send to backend
            const result = await createOrUpdateBulkPostsHandler(transformedPosts, publicIdentifier);
            
            return result;
            
        } catch (error) {
            console.error("Error syncing profile posts:", error);
            throw error;
        }
    }


    /**
     * Get workspace tracked posts with pagination
     */
    const getWorkspacePostsHandler = async (params?: { page?: number, limit?: number }): Promise<IWorkspacePostsResponse | null> => {
        try {
            const result = await getWorkspacePosts(params);
            return result;
        } catch (error) {
            console.error("Error getting workspace posts:", error);
            throw error;
        }
    }

    /**
     * Get profiles with tracked connections
     */
    const getProfilesWithTrackedConnectionsHandler = async () => {
        try {
            const result = await getProfilesWithTrackedConnections();
            return result;
        } catch (error) {
            console.error("Error getting profiles with tracked connections:", error);
            throw error;
        }
    }

    /**
     * Periodic posts fetch handler - checks every 10 seconds if more than configured interval has passed
     * since last fetch and triggers the action
     */
    const periodicPostsFetchHandler = useCallback(async (user: any) => {
        // Check if already executing to prevent overlapping executions
        if (isExecutingRef.current) {
            console.log('periodicPostsFetchHandler already executing - skipping...');
            return;
        }

        isExecutingRef.current = true;
        
        try {
            console.log('periodicPostsFetchHandler called for user:', user._id);
            console.log('user.lastFetchPostsDate:', user.lastFetchPostsDate);
            
            if (!user?.lastFetchPostsDate) {
                console.log('No lastFetchPostsDate found - first time, calling API...');
                const result = await updateLastFetchPostsDateHandler();
                console.log('First time posts fetch result:', result);
                return;
            }

            const now = new Date();
            const lastFetch = new Date(user.lastFetchPostsDate);
            const timeDifference = now.getTime() - lastFetch.getTime();
            const fetchIntervalMs = FETCH_INTERVAL_MINUTES * 60 * 1000; // Convert minutes to milliseconds

            console.log('Time difference:', timeDifference, 'ms (', Math.round(timeDifference / 1000), 'seconds)');
            console.log(`Fetch interval threshold: ${fetchIntervalMs} ms (${FETCH_INTERVAL_MINUTES} minute${FETCH_INTERVAL_MINUTES !== 1 ? 's' : ''})`);

            if (timeDifference > fetchIntervalMs) {
                console.log(`More than ${FETCH_INTERVAL_MINUTES} minute${FETCH_INTERVAL_MINUTES !== 1 ? 's' : ''} passed since last posts fetch - triggering API call...`);
                
                const result = await updateLastFetchPostsDateHandler();
                console.log('Posts fetch action result:', result);
                
                // Get profiles with tracked connections
                const profilesResult = await getProfilesWithTrackedConnectionsHandler();
                console.log('Tracked connections response:', profilesResult);
                
                if (profilesResult && profilesResult.profiles) {
                    // Process each profile with tracked connections
                    for (let i = 0; i < profilesResult.profiles.length; i++) {
                        const profile = profilesResult.profiles[i];
                        console.log('Processing profile:', profile);
                        
                        if (profile && profile.profileId && profile.publicIdentifier) {
                            try {
                                // Construct the profile URN
                                const profileUrn = `urn:li:fsd_profile:${profile.profileId}`;
                                
                                const profilePosts = await getProfilePosts(profileUrn);
                                //const profilePosts: any[] = [];
                                
                                if (profilePosts) {
                                    // Transform and sync posts to backend
                                    await syncProfilePostsHandler(profilePosts, profile.publicIdentifier);
                                    console.log("Posts successfully synced to backend for:", profile.publicIdentifier);
                                } else {
                                    console.log("SKIPPING transformProfilePosts because profilePosts is:", profilePosts);
                                }
                            } catch (error) {
                                console.error("Error syncing posts for profile:", profile.publicIdentifier, error);
                            }
                        }
                        
                        // Add random pause between iterations (except for the last iteration)
                        if (i < profilesResult.profiles.length - 1) {
                            const pauseDuration = getRandomPauseDuration();
                            const pauseSeconds = Math.round(pauseDuration / 1000);
                            console.log(`Pausing for ${pauseSeconds} seconds before processing next profile...`);
                            await sleep(pauseDuration);
                        }
                    }
                } else {
                    console.log('No profiles found in profilesResult.');
                }
                
                console.log('Posts fetch action completed');
            } else {
                console.log(`Less than ${FETCH_INTERVAL_MINUTES} minute${FETCH_INTERVAL_MINUTES !== 1 ? 's' : ''} passed - skipping API call`);
            }
        } catch (error) {
            console.error("Error in periodic posts fetch:", error);
        } finally {
            // Always reset the execution flag when done
            isExecutingRef.current = false;
        }
    }, [updateLastFetchPostsDateHandler])

    /**
     * Starts the periodic posts fetch check that runs at configurable intervals
     * Uses recursive setTimeout to ensure each execution waits for the previous one to complete
     */
    const startPeriodicPostsCheck = useCallback(() => {
        console.log(`startPeriodicPostsCheck called - setting up sequential check (${TRIGGER_INTERVAL_SECONDS}s interval)`);
        
        let timeoutId: NodeJS.Timeout;
        let isStopped = false;
        
        const periodicCheck = async () => {
            if (isStopped) {
                console.log('Periodic check stopped');
                return;
            }
            
            console.log('Periodic check triggered, user:', userRef.current?._id);
            
            try {
                if (userRef.current) {
                    await periodicPostsFetchHandler(userRef.current);
                } else {
                    console.log('No user found in userRef');
                }
            } catch (error) {
                console.error('Error in periodic check:', error);
            }
            
            // Schedule next check only after current one completes
            if (!isStopped) {
                timeoutId = setTimeout(periodicCheck, TRIGGER_INTERVAL_SECONDS * 1000);
            }
        };

        // Start the first check
        periodicCheck();
        
        // Return a function to stop the periodic check
        return () => {
            isStopped = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            console.log('Periodic posts check stopped');
        };
    }, [periodicPostsFetchHandler])

    /**
     * Like a profile post
     */
    const likeProfilePostsHandler = async (payload: any) => {
       try {
            const result = await likeProfilePosts(payload);
            console.log('Like profile post result:', result);
            return result;
        } catch (error) {
            console.error('Error liking profile post:', error);
            throw error;
        }
    }
    const commentProfilePostsHandler = async (payload: any) => {
        try {
            const result = await commentProfilePosts(payload);
            return result;
        } catch (error) {
            console.error('Error commenting on profile post:', error);
            throw error;
        }
    }

    const editProfilePostCommentHandler = async (payload: any) => {
        try {
            const result = await editProfilePostComment(payload);
            return result;
        } catch (error) {
            console.error('Error editing profile post comment:', error);
            throw error;
        }
    }

    const deleteProfilePostCommentHandler = async (commentUrn: string) => {
        try {
            const result = await deleteProfilePostComment(commentUrn);
            return result;
        } catch (error) {
            console.error('Error deleting profile post comment:', error);
            throw error;
        }
    }

    const updateConnectionPostsHandler = async (payload: any) => {
        try {
            const result = await updateConnectionPosts(payload);
            return result;
        } catch (error) {
            console.error('Error updating connection posts:', error);
            throw error;
        }
    }

    const updateWeeklyGoalHandler = async (weeklyGoal: number) => {
        try {
            const result = await updateWeeklyGoal(weeklyGoal);
            if (result?.success) {
                return result;
            } else {
                showErrorToast({ message: result?.message || "Error updating weekly goal" });
            }
        } catch (error) {
            console.error('Error updating weekly goal:', error);
            throw error;
        }
    }

    /**
     * Manual sync for one custom feed's members (LinkedIn → LeadDelta).
     * Does not touch the global periodic sync / lastFetchPostsDate.
     */
    const syncFeedConnectionsHandler = useCallback(async (
        profiles: TFeedSyncProfile[],
        opts?: {
            shouldCancel?: () => boolean;
            onProgress?: (progress: TFeedSyncProgress) => void;
        },
    ): Promise<{ synced: number; failed: number; skipped: number; cancelled: boolean }> => {
        const total = profiles.length;
        let synced = 0;
        let failed = 0;
        let skipped = 0;

        for (let i = 0; i < profiles.length; i++) {
            if (opts?.shouldCancel?.()) {
                return { synced, failed, skipped, cancelled: true };
            }

            const profile = profiles[i];
            const { publicIdentifier, profileId } = resolveFeedSyncIds(profile);
            const displayName = (
                profile.name
                || [profile.firstName, profile.lastName].filter(Boolean).join(' ')
                || publicIdentifier
                || 'Unknown'
            ).trim();

            opts?.onProgress?.({
                index: i + 1,
                total,
                displayName,
                publicIdentifier: publicIdentifier || undefined,
                status: 'syncing',
                synced,
                failed,
                skipped,
            });

            if (!profileId || !publicIdentifier) {
                skipped += 1;
                opts?.onProgress?.({
                    index: i + 1,
                    total,
                    displayName,
                    publicIdentifier: publicIdentifier || undefined,
                    status: 'skipped',
                    synced,
                    failed,
                    skipped,
                });
            } else {
                try {
                    const profileUrn = `urn:li:fsd_profile:${profileId}`;
                    const profilePosts = await getProfilePosts(profileUrn);
                    if (opts?.shouldCancel?.()) {
                        return { synced, failed, skipped, cancelled: true };
                    }
                    if (profilePosts) {
                        await syncProfilePostsHandler(profilePosts, publicIdentifier);
                        synced += 1;
                        opts?.onProgress?.({
                            index: i + 1,
                            total,
                            displayName,
                            publicIdentifier,
                            status: 'success',
                            synced,
                            failed,
                            skipped,
                        });
                    } else {
                        skipped += 1;
                        opts?.onProgress?.({
                            index: i + 1,
                            total,
                            displayName,
                            publicIdentifier,
                            status: 'skipped',
                            synced,
                            failed,
                            skipped,
                        });
                    }
                } catch (error) {
                    console.error('Error syncing feed profile posts:', publicIdentifier, error);
                    failed += 1;
                    opts?.onProgress?.({
                        index: i + 1,
                        total,
                        displayName,
                        publicIdentifier,
                        status: 'failed',
                        synced,
                        failed,
                        skipped,
                    });
                }
            }

            if (i < profiles.length - 1 && !opts?.shouldCancel?.()) {
                await sleep(getManualFeedSyncPauseMs());
            }
        }

        return { synced, failed, skipped, cancelled: false };
    }, [getProfilePosts, syncProfilePostsHandler]);

    /**
     * Manual sync for all unique tracked profiles (same source as background periodic sync).
     * Updates lastFetchPostsDate so the background job does not immediately re-run.
     */
    const syncTrackedConnectionsHandler = useCallback(async (
        opts?: {
            shouldCancel?: () => boolean;
            onProgress?: (progress: TFeedSyncProgress) => void;
        },
    ): Promise<{ synced: number; failed: number; skipped: number; cancelled: boolean; total: number }> => {
        await updateLastFetchPostsDateHandler();
        const profilesResult = await getProfilesWithTrackedConnections();
        const profiles = profilesResult?.profiles || [];
        if (!profiles.length) {
            return { synced: 0, failed: 0, skipped: 0, cancelled: false, total: 0 };
        }
        const result = await syncFeedConnectionsHandler(profiles, opts);
        return { ...result, total: profiles.length };
    }, [updateLastFetchPostsDateHandler, getProfilesWithTrackedConnections, syncFeedConnectionsHandler]);

    return {
        createOrUpdateBulkPostsHandler,
        syncProfilePostsHandler,
        getWorkspacePostsHandler,
        getProfilesWithTrackedConnectionsHandler,
        periodicPostsFetchHandler,
        startPeriodicPostsCheck,
        likeProfilePostsHandler,
        commentProfilePostsHandler,
        updateConnectionPostsHandler,
        updateWeeklyGoalHandler,
        editProfilePostCommentHandler,
        deleteProfilePostCommentHandler,
        syncFeedConnectionsHandler,
        syncTrackedConnectionsHandler,
    };
}
